import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchActiveWindow, fetchMealPlanDays, fetchRecipesForMeals, organizeDaysWithRecipes, formatDate, isToday, DayPlan } from '@/lib/meal-plans';
import LoadingSpinner from '@/components/LoadingSpinner';
import styles from './MealPlanPage.module.css';

const SUBSCRIBE_URL = 'https://peapilpublishing.com/pages/peapil-health';

function MealCard({ meal }: { meal: DayPlan['meals'][0] }) {
  if (!meal.recipe) return null;

  return (
    <div className={styles.mealCard}>
      <Link to={`/recipes/${meal.recipe.id}`} className={styles.mealLink}>
        <img
          src={meal.recipe.image}
          alt={meal.recipe.title}
          className={styles.mealImage}
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/placeholder-recipe.png';
          }}
        />
        <div className={styles.mealInfo}>
          <h4 className={styles.mealTitle}>{meal.recipe.title}</h4>
          <span className={styles.mealCalories}>{meal.recipe.calories} cal</span>
        </div>
      </Link>
    </div>
  );
}

export default function MealPlanPage() {
  const { user, hasFullAccess } = useAuth();
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);

  // All hooks must be called before any early returns
  const { data: window } = useQuery({
    queryKey: ['meal-plan-window', user?.id],
    queryFn: () => fetchActiveWindow(user!.id),
    enabled: !!user?.id && hasFullAccess,
  });

  const { data: days } = useQuery({
    queryKey: ['meal-plan-days', window?.id],
    queryFn: () => fetchMealPlanDays(window!.id),
    enabled: !!window && hasFullAccess,
  });

  const recipeIds = useMemo(() => {
    if (!days) return [];
    const ids = new Set<string>();
    days.forEach(day => {
      (day.meals || []).forEach(meal => {
        if (meal.recipeId) ids.add(meal.recipeId);
      });
    });
    return Array.from(ids);
  }, [days]);

  const { data: recipeMap } = useQuery({
    queryKey: ['meal-plan-recipes', recipeIds],
    queryFn: () => fetchRecipesForMeals(recipeIds),
    enabled: recipeIds.length > 0 && hasFullAccess,
  });

  const organizedDays = useMemo(() => {
    if (!days) return [];
    return organizeDaysWithRecipes(days, recipeMap || new Map());
  }, [days, recipeMap]);

  const selectedDay = organizedDays[selectedDateIndex];

  // Show subscribers-only message for free users
  if (!hasFullAccess) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <h1 className={styles.title}>
              <Calendar size={28} />
              Meal Plan
            </h1>
          </div>
        </header>
        <div className={styles.subscriberOnly}>
          <p>Meal planning is a premium feature. Subscribe to create personalized weekly meal plans, weekly shopping lists tailored to your health goals.</p>
          <a href={SUBSCRIBE_URL} target="_blank" rel="noopener noreferrer" className={styles.subscribeButton}>
            Subscribe Now
          </a>
        </div>
      </div>
    );
  }

  if (!user) return <LoadingSpinner />;

  if (!window || organizedDays.length === 0) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.headerTop}>
            <h1 className={styles.title}>
              <Calendar size={28} />
              Meal Plan
            </h1>
          </div>
        </header>
        <div className={styles.emptyState}>
          <Calendar size={48} />
          <h2>No Active Meal Plan</h2>
          <p>Start a meal plan from the mobile app to see your scheduled meals here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <h1 className={styles.title}>
            <Calendar size={28} />
            Meal Plan
          </h1>
        </div>
        <p className={styles.subtitle}>Your {window.cycle_length || organizedDays.length}-day meal plan</p>
      </header>

      <div className={styles.dateNavigation}>
        <button
          className={styles.navButton}
          onClick={() => setSelectedDateIndex(Math.max(0, selectedDateIndex - 1))}
          disabled={selectedDateIndex === 0}
        >
          <ChevronLeft size={24} />
        </button>

        <div className={styles.dateScroller}>
          {organizedDays.map((day, index) => (
            <button
              key={day.id}
              className={`${styles.dateButton} ${index === selectedDateIndex ? styles.dateActive : ''} ${isToday(day.date) ? styles.dateToday : ''}`}
              onClick={() => setSelectedDateIndex(index)}
            >
              <span className={styles.dateLabel}>{isToday(day.date) ? 'Today' : formatDate(day.date)}</span>
              <span className={styles.dateCalories}>{day.plannedCalories} cal</span>
            </button>
          ))}
        </div>

        <button
          className={styles.navButton}
          onClick={() => setSelectedDateIndex(Math.min(organizedDays.length - 1, selectedDateIndex + 1))}
          disabled={selectedDateIndex === organizedDays.length - 1}
        >
          <ChevronRight size={24} />
        </button>
      </div>

      {selectedDay && (
        <div className={styles.dayContent}>
          <div className={styles.dayHeader}>
            <h2 className={styles.dayTitle}>
              {isToday(selectedDay.date) ? "Today's Meals" : formatDate(selectedDay.date)}
            </h2>
            <span className={styles.dayCalories}>{selectedDay.plannedCalories} total calories</span>
          </div>

          {selectedDay.meals.length > 0 ? (
            <div className={styles.mealsList}>
              {selectedDay.meals.map((meal) => (
                <MealCard key={meal.id} meal={meal} />
              ))}
            </div>
          ) : (
            <div className={styles.noMeals}>
              <p>No meals planned for this day.</p>
              <Link to="/recipes" className={styles.addMealsLink}>Browse Recipes</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
